type SplitCandidateBlock = {
	listItem?: string | null;
	level?: number | null;
	style?: string | null;
	_type?: string;
};

/** Minimum top-level blocks before a second mid-content CTA is added. */
const LONG_ARTICLE_BLOCK_COUNT = 18;
/** Minimum top-level blocks before a third mid-content CTA is added. */
const LONGEST_ARTICLE_BLOCK_COUNT = 30;
/** Minimum blocks between consecutive CTA split indices. */
const MIN_SPLIT_GAP = 5;

const isListItem = (block: SplitCandidateBlock | undefined): boolean =>
	Boolean(block?.listItem);

/**
 * True when inserting a CTA at `index` would not cut a contiguous list run.
 * Index `i` means blocks `[0, i)` render before the CTA.
 */
const isSafeSplitIndex = (
	blocks: SplitCandidateBlock[],
	index: number,
): boolean => {
	if (index <= 0 || index >= blocks.length) return true;
	return !(isListItem(blocks[index - 1]) && isListItem(blocks[index]));
};

const idealSplitIndex = (blockCount: number): number => {
	if (blockCount <= 1) return 0;
	if (blockCount <= 3) return 1;
	return Math.floor(blockCount / 2);
};

/**
 * Finds the nearest list-safe split index to `ideal`, preferring later indices
 * then earlier ones. Returns `null` when no safe index exists in (0, length).
 */
const nearestSafeSplitIndex = (
	blocks: SplitCandidateBlock[],
	ideal: number,
): number | null => {
	const blockCount = blocks.length;
	if (blockCount <= 1) return 0;

	const clamped = Math.min(Math.max(ideal, 1), blockCount);

	for (let index = clamped; index <= blockCount; index++) {
		if (isSafeSplitIndex(blocks, index)) return index;
	}

	for (let index = clamped - 1; index >= 1; index--) {
		if (isSafeSplitIndex(blocks, index)) return index;
	}

	return null;
};

const ctaCountForLength = (blockCount: number): number => {
	if (blockCount >= LONGEST_ARTICLE_BLOCK_COUNT) return 3;
	if (blockCount >= LONG_ARTICLE_BLOCK_COUNT) return 2;
	return 1;
};

const idealFractions = (count: number): number[] => {
	if (count === 1) return [0.5];
	if (count === 2) return [1 / 3, 2 / 3];
	return [1 / 4, 1 / 2, 3 / 4];
};

/**
 * Returns the index at which to insert a mid-content CTA in a portable-text
 * body. Aims for the middle while avoiding splits inside bullet/number lists.
 *
 * @param blocks - Top-level portable text blocks
 * @returns Split index (blocks `[0, index)` render before the CTA)
 */
export const getMidContentSplitIndex = (
	blocks: SplitCandidateBlock[],
): number => {
	const blockCount = blocks.length;
	if (blockCount <= 1) return 0;

	return (
		nearestSafeSplitIndex(blocks, idealSplitIndex(blockCount)) ?? blockCount
	);
};

/**
 * Returns one to three list-safe split indices for mid-content membership CTAs.
 * Short bodies get a single mid split; longer bodies get 2–3 evenly spaced
 * splits with a minimum gap between them.
 *
 * @param blocks - Top-level portable text blocks
 * @returns Ascending split indices (blocks `[0, index)` render before each CTA)
 */
export const getContentMembershipSplitIndices = (
	blocks: SplitCandidateBlock[],
): number[] => {
	const blockCount = blocks.length;
	if (blockCount <= 1) return [0];

	const ctaCount = ctaCountForLength(blockCount);
	const fractions = idealFractions(ctaCount);
	const indices: number[] = [];

	for (const fraction of fractions) {
		const ideal = Math.floor(blockCount * fraction);
		const safe = nearestSafeSplitIndex(blocks, ideal);
		if (safe === null) continue;

		const previous = indices[indices.length - 1];
		if (previous !== undefined && safe - previous < MIN_SPLIT_GAP) {
			const pushed = nearestSafeSplitIndex(blocks, previous + MIN_SPLIT_GAP);
			if (
				pushed !== null &&
				pushed > previous &&
				pushed < blockCount &&
				!indices.includes(pushed)
			) {
				indices.push(pushed);
			}
			continue;
		}

		if (safe > 0 && safe < blockCount && !indices.includes(safe)) {
			indices.push(safe);
		} else if (safe === blockCount && indices.length === 0) {
			indices.push(safe);
		}
	}

	if (indices.length === 0) {
		return [getMidContentSplitIndex(blocks)];
	}

	return indices;
};
